import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentAppUserId } from '@/hooks/useCurrentAppUser';
import { toast } from 'sonner';

// Types
export interface StageTag {
  id: string;
  stage: string;
  name: string;
  color: string | null;
  sort_order: number | null;
  is_active: boolean | null;
}

export interface DealTag {
  id: string;
  deal_id: string;
  tag_id: string;
  assigned_by: string | null;
  assigned_at: string | null;
  tag?: StageTag;
}

// Query keys
const STAGE_TAGS_KEY = 'stage-tags';
const DEAL_TAGS_KEY = 'deal-tags';

/**
 * Fetch all active tags for a specific stage
 */
export function useStageTagsQuery(stage: string | undefined) {
  return useQuery({
    queryKey: [STAGE_TAGS_KEY, stage],
    queryFn: async () => {
      if (!stage) return [];
      
      const { data, error } = await supabase
        .from('stage_tags')
        .select('id, stage, name, color, sort_order, is_active')
        .eq('stage', stage)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return (data || []) as StageTag[];
    },
    enabled: !!stage,
    staleTime: 5 * 60 * 1000, // 5 minutes - tags don't change often
  });
}

/**
 * Fetch all tags assigned to a specific deal (with tag details)
 */
export function useDealTagsQuery(dealId: string | undefined) {
  return useQuery({
    queryKey: [DEAL_TAGS_KEY, dealId],
    queryFn: async () => {
      if (!dealId) return [];
      
      const { data, error } = await supabase
        .from('deal_tags')
        .select(`
          id,
          deal_id,
          tag_id,
          assigned_by,
          assigned_at,
          tag:stage_tags(id, stage, name, color, sort_order, is_active)
        `)
        .eq('deal_id', dealId);

      if (error) throw error;
      
      // Flatten the tag relationship
      return (data || []).map(dt => ({
        ...dt,
        tag: Array.isArray(dt.tag) ? dt.tag[0] : dt.tag,
      })) as DealTag[];
    },
    enabled: !!dealId,
  });
}

/**
 * Add a tag to a deal
 */
export function useAddDealTag() {
  const queryClient = useQueryClient();
  const currentUserId = useCurrentAppUserId();

  return useMutation({
    mutationFn: async ({ dealId, tagId }: { dealId: string; tagId: string }) => {
      const { data, error } = await supabase
        .from('deal_tags')
        .insert({
          deal_id: dealId,
          tag_id: tagId,
          assigned_by: currentUserId,
        })
        .select()
        .single();

      if (error) {
        // Handle duplicate key error gracefully
        if (error.code === '23505') {
          throw new Error('Tag já atribuída');
        }
        throw error;
      }
      return data;
    },
    onSuccess: (_, { dealId }) => {
      queryClient.invalidateQueries({ queryKey: [DEAL_TAGS_KEY, dealId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao adicionar tag');
    },
  });
}

/**
 * Remove a tag from a deal
 */
export function useRemoveDealTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ dealId, tagId }: { dealId: string; tagId: string }) => {
      const { error } = await supabase
        .from('deal_tags')
        .delete()
        .eq('deal_id', dealId)
        .eq('tag_id', tagId);

      if (error) throw error;
    },
    onSuccess: (_, { dealId }) => {
      queryClient.invalidateQueries({ queryKey: [DEAL_TAGS_KEY, dealId] });
    },
    onError: () => {
      toast.error('Erro ao remover tag');
    },
  });
}

/**
 * Combined hook for managing deal tags
 */
export function useDealTags(dealId: string | undefined, stage: string | undefined) {
  const stageTags = useStageTagsQuery(stage);
  const dealTags = useDealTagsQuery(dealId);
  const addTag = useAddDealTag();
  const removeTag = useRemoveDealTag();

  // Set of assigned tag IDs for quick lookup
  const assignedTagIds = new Set(dealTags.data?.map(dt => dt.tag_id) || []);

  // Check if a tag is assigned
  const isTagAssigned = (tagId: string) => assignedTagIds.has(tagId);

  // Toggle tag assignment
  const toggleTag = async (tagId: string) => {
    if (!dealId) return;
    
    if (isTagAssigned(tagId)) {
      await removeTag.mutateAsync({ dealId, tagId });
    } else {
      await addTag.mutateAsync({ dealId, tagId });
    }
  };

  return {
    // Available tags for the stage
    availableTags: stageTags.data || [],
    availableTagsLoading: stageTags.isLoading,
    
    // Tags assigned to the deal
    assignedTags: dealTags.data || [],
    assignedTagsLoading: dealTags.isLoading,
    
    // Helpers
    isTagAssigned,
    toggleTag,
    
    // Mutation states
    isToggling: addTag.isPending || removeTag.isPending,
  };
}
